/* eslint-disable react/prop-types */
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLocalStorage } from './useLocalStorage'
import axios from 'axios'
import { API_URL, ApiService } from './services/api.service'
import { DEV_PLAN_OVERRIDE_KEY, planHasFeature, resolvePlan } from './utils/plan'
const AuthContext = createContext('auth')

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useLocalStorage('user', null)
  // Dev-only plan override (light/pro) for testing. Null in production means the
  // plan comes from the backend via `user.plan`. See src/utils/plan.js.
  const [devPlanOverride, setDevPlanOverride] = useLocalStorage(DEV_PLAN_OVERRIDE_KEY, null)
  const [showLoginToast, setShowLoginToast] = useState(false)
  const navigate = useNavigate()

  // Must be stable across renders: the auth interceptor is (re)attached to this
  // exact instance by AxiosInterceptor keyed on `user`. Recreating it on every
  // render (e.g. when toggling the plan) would leave the new instance without
  // interceptors, so requests would go out with no Bearer token and 401.
  const axiosInstance = useMemo(() => axios.create({ baseURL: API_URL }), [])

  const apiService = useMemo(
    () => (user ? new ApiService(axiosInstance, user.pharmacy) : null),
    [axiosInstance, user],
  )

  const login = (data) => {
    setUser(data)
    setShowLoginToast(true)
    navigate('/dashboard', { replace: true })
  }

  const logout = (expired) => {
    setUser(null)
    if (expired) {
      window.location.replace('/login?session_expired=1')
    } else {
      window.location.replace('/login')
    }
  }

  const apiSessionIsOn = () => {
    if (!user) {
      return false
    }

    try {
      const token = user.access_token
      const payload = JSON.parse(atob(token.split('.')[1]))

      // check if the token has expired
      return payload.exp > Date.now() / 1000
    } catch (e) {
      return false
    }
  }

  // The dev plan toggle is a local testing aid only. In production the plan
  // always comes from the backend (user.plan) — never from a stored override.
  const effectiveOverride = process.env.NODE_ENV === 'production' ? null : devPlanOverride
  const plan = resolvePlan(user, effectiveOverride)
  const hasFeature = (feature) => planHasFeature(plan, feature)

  const value = useMemo(
    () => ({
      user,
      login,
      logout,
      apiSessionIsOn,
      showLoginToast,
      setShowLoginToast,
      apiService,
      axiosInstance,
      plan,
      hasFeature,
      devPlanOverride,
      setDevPlanOverride,
    }),
    [user, plan, devPlanOverride],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  return useContext(AuthContext)
}
